import { FormHelperText, SelectChangeEvent } from '@mui/material';
import {
  ChangeEventHandler,
  createElement,
  FocusEventHandler,
  ReactNode,
} from 'react';
import { FormValues, UseFormResult } from '../hooks/useForm';

/**
 * General props for any MaterialUI input. Specifically designed for text based inputs.
 */
export interface MaterialBaseProps {
  /** Name value for the input */
  name: string;
  /** Default blur handler for the input. Will set the property as "touched". */
  onBlur: FocusEventHandler<HTMLElement>;
  /** The value that will be set for the input. It wil be the one inside "values[property]", but as this is a generic type, "any" will be used unless a specific typing is given */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  value: any;
  /** Will have the value set in the main function options or `false` by default. */
  disabled: boolean;
  /** Value that indicates if the field has a visible error */
  error: boolean;
}

export interface MaterialInputProps extends MaterialBaseProps {
  /** Default change handler for the input. Just updates the values[property] with the "target.value" (which is a string)  */
  onChange: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
}

/**
 * Specific complete props for a TextField.
 */
export interface MaterialTextFieldProps extends MaterialInputProps {
  helperText?: string;
}

export interface MaterialSelectProps extends MaterialBaseProps {
  onChange: (event: SelectChangeEvent) => void;
}
export interface MaterialCheckboxProps extends MaterialBaseProps {
  onChange: ChangeEventHandler<HTMLInputElement>;
}

export interface MaterialInputsFormHelper<T extends FormValues> {
  /** Returns base props that all Material inputs can accept, without a change handler (but including blur handler).
   *
   * If just one of the properties is needed, this should be the function to use as it is the fastest.
   */
  getBaseProps: (field: keyof T) => MaterialBaseProps;
  /** Gets all the base props with a default "change" handler. It is specifically designed to be used with a spread operator directly
   * on inputs:
   * ```jsx
   * <SomeInput {...getInputProps("field")} />
   * ```
   */
  getInputProps: (field: keyof T, touch?: boolean) => MaterialInputProps;
  /** Gets all the base props, the default "change" handler and the "helperText" to display in case of error. Specifically designed to be
   * used with a spread operator directly on "TextField" components.
   * ```jsx
   * <TextField {...getTextFieldProps("field")} />
   * ```
   */
  getTextFieldProps: (
    field: keyof T,
    touch?: boolean,
  ) => MaterialTextFieldProps;
  /** Gets all the base props with the default "change" handler. Specifically designed to be used with a spread operator directly on
   * "Select" components.
   * ```jsx
   * <Select {...getSelectProps("field")} />
   * ```
   */
  getSelectProps: (field: keyof T, touch?: boolean) => MaterialSelectProps;
  /** Gets all the base props with the default "change" handler. Specifically designed to be used with a spread operator directly on
   * "Checkbox" components.
   * ```jsx
   * <Checkbox {...getCheckboxProps("field")} />
   * ```
   */
  getCheckboxProps: (field: keyof T, touch?: boolean) => MaterialCheckboxProps;
  /**
   * Includes the logic to render a "FormHelperText" component with the error message for a field if there is one.
   */
  renderError: (field: keyof T) => ReactNode;
}

export interface GetMaterialInputsFormHelperOptions {
  shouldInputsBeDisabled: boolean;
}

export type FormStateResultFields<T extends FormValues> = Pick<
  UseFormResult<T>,
  'values' | 'errors' | 'updateValues' | 'touchField'
>;

/**
 * Utility function that returns a series of helpers to get the appropriate props for inputs on complex forms, so
 * repeated code can be evaded.
 */
const getMaterialInputsFormHelper = <T extends FormValues>(
  { values, errors, updateValues, touchField }: FormStateResultFields<T>,
  options = {} as Partial<GetMaterialInputsFormHelperOptions>,
): MaterialInputsFormHelper<T> => {
  const { shouldInputsBeDisabled = false } = options;
  const inputPropsMap: Partial<Record<keyof T, MaterialBaseProps>> = {};

  const getBaseProps: MaterialInputsFormHelper<T>['getBaseProps'] = field => {
    let inputProps = inputPropsMap[field];
    if (!inputProps) {
      inputProps = {
        name: field as string,
        onBlur: () => touchField(field),
        disabled: shouldInputsBeDisabled,
        error: Boolean(errors[field]),
        value: values[field],
      };
      inputPropsMap[field] = inputProps;
    }
    return inputProps;
  };

  return {
    getBaseProps,
    getInputProps: (field, touch) => ({
      ...getBaseProps(field),
      onChange: event =>
        updateValues({ [field]: event.target.value } as Partial<T>, touch),
    }),
    getTextFieldProps: (field, touch) => ({
      ...getBaseProps(field),
      onChange: event =>
        updateValues({ [field]: event.target.value } as Partial<T>, touch),
      helperText: errors[field],
    }),
    getSelectProps: (field, touch = true) => ({
      ...(getBaseProps(field) as unknown as MaterialSelectProps),
      onChange: event =>
        updateValues({ [field]: event.target.value } as Partial<T>, touch),
    }),
    getCheckboxProps: (field, touch = true) => ({
      ...(getBaseProps(field) as unknown as MaterialSelectProps),
      onChange: event =>
        updateValues({ [field]: event.target.checked } as Partial<T>, touch),
    }),
    renderError: field =>
      errors[field] && createElement(FormHelperText, null, errors[field]),
  };
};

export default getMaterialInputsFormHelper;
